import {
  GroupItem,
  ManagementIcon,
  ContentDrawer,
  SettingItem,
  SettingSubItem,
  TransferGroupOwner,
  MemberNotifications,
  GroupAdmins,
  tools,
} from '@halo/ui-chat-react';
import { Switch, toaster } from '@halo/ui-react';
import { Stack, Box } from '@chakra-ui/react';
import { useMemo, memo } from 'react';
import { useGroupMembers, type SessionStore } from '@halo/chat-sdk-react';
import type { GroupMember, GroupOptions } from '@halo/chat-sdk';
import { GroupMemberRole, GroupOptionDisableEnum, GroupOptionKey } from '@halo/chat-sdk';
import { GroupOptionBodyEnum } from '@halo/chat-sdk';
import { useStaticAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';

export interface GroupManagementProps {
  sessionStore: SessionStore;
  isGroupOwner?: boolean;
  onMemberAvatarClick?: (member: GroupMember) => Promise<void>;
  onTransferGroupOwnerClick: (member: GroupMember) => Promise<any>;
  closeAllClick?: () => void;
}

export const MAX_ADMIN_COUNT = 10;

const GroupManagement = ({
  sessionStore,
  isGroupOwner,
  onMemberAvatarClick,
  onTransferGroupOwnerClick,
  closeAllClick,
}: GroupManagementProps) => {
  const { t } = useTranslation();
  const setGroupAddAdminIsOpen = useActionsStore((store) => store.setGroupAddAdminIsOpen);
  const { client } = useStaticAppStore();
  const { session = null, groupOptions } = sessionStore;
  const disabledManagement = false;

  const baseOptions = [
    {
      label: t('chatSettings.everybody'),
      value: GroupOptionBodyEnum.Everyone.toString(),
    },
    {
      label: t('chatSettings.ownerAndAdmin'),
      value: GroupOptionBodyEnum.Admin.toString(),
    },
  ];
  const optionsWithNobody = [
    ...baseOptions,
    { label: t('chatSettings.nobody'), value: GroupOptionBodyEnum.Nobody.toString() },
  ];

  const groupDisallowSendInfo = useMemo(() => {
    return sessionStore?.groupDisallowSendInfo;
  }, [sessionStore?.groupDisallowSendInfo]);

  const convertToBoolean = (value?: number): boolean => {
    return value === GroupOptionDisableEnum.Enable;
  };

  const {
    memberJoinNotice,
    memberLeaveNotice,
    memberInfoEdit,
    memberAtAll,
    memberSendMessage,
    memberMediaCall,
    memberPrivateMessage,
    groupGlobalSearch,
    enterAudit,
    hideMember,
    restrictCoping,
    restrictForward,
    restrictDownload,
    // restrictMemberRange,
  } = useMemo(() => {
    return {
      memberJoinNotice: groupOptions?.member_join_notice?.val,
      memberLeaveNotice: groupOptions?.member_leave_notice?.val,
      memberInfoEdit: groupOptions?.info_edit?.val,
      memberAtAll: groupOptions?.at_all?.val,
      memberSendMessage: groupOptions?.send_message?.val,
      memberMediaCall: groupOptions?.media_call?.val,
      memberPrivateMessage: groupOptions?.private_message?.val,
      groupGlobalSearch: groupOptions?.global_search?.val,
      enterAudit: groupOptions?.enter_audit?.val,
      hideMember: groupOptions?.hide_member?.val,
      restrictCoping: groupOptions?.restrict_coping?.val,
      restrictForward: groupOptions?.restrict_forward?.val,
      restrictDownload: groupOptions?.restrict_download?.val,
      restrictMemberRange: groupOptions?.restrict_member_range?.val,
    };
  }, [groupOptions]);

  // const restrictedMode = useMemo(() => {
  //   return convertToBoolean(restrictMemberRange);
  // }, [restrictMemberRange]);
  const restrictedMode = useMemo(() => {
    const keys = [
      GroupOptionKey.RESTRICT_COPING,
      GroupOptionKey.RESTRICT_FORWARD,
      GroupOptionKey.RESTRICT_DOWNLOAD,
    ];
    return keys.some((key) => convertToBoolean(groupOptions?.[key]?.val));
  }, [groupOptions]);

  const { members, run, removeEventListener } = useGroupMembers(session?.chat_id);

  const { adminStr, adminMembers } = useMemo(() => {
    const adminMembers = members.filter((member) => member.role === GroupMemberRole.ADMIN);
    const adminAndOwnerMembers = members.filter(
      (member) => member.role === GroupMemberRole.ADMIN || member.role === GroupMemberRole.OWNER
    );
    return {
      adminStr: `${adminAndOwnerMembers.length}/${members.length}`,
      adminMembers,
      adminAndOwnerMembers,
    };
  }, [members]);

  const getOptionLabel = (value?: string, opts = baseOptions) => {
    if (!value) return opts[0]?.label ?? '';
    return opts.find((item) => item.value === value)?.label ?? opts[0]?.label ?? '';
  };

  const handleMemberNotificationChange = (key: GroupOptionKey) => {
    return async (value: string) => {
      if (!groupOptions || !client) return;
      const numValue = Number(value);
      try {
        await client.Api.session.setGroupOptions(groupOptions.group_id, {
          [key]: numValue,
        });
      } catch (error) {
        toaster.error({
          title: t('tips.failedToSetGroupOptions'),
        });
        console.error('setGroupOptions', error);
      }
    };
  };

  const handleSwitchChange = (key: GroupOptionKey, checked: boolean) => {
    if (!groupOptions || !client) return;
    const reversedKeys = [
      GroupOptionKey.RESTRICT_COPING,
      GroupOptionKey.RESTRICT_FORWARD,
      GroupOptionKey.RESTRICT_DOWNLOAD,
    ];
    const isReversed = reversedKeys.includes(key);
    client.Api.session.setGroupOptions(groupOptions.group_id, {
      [key]: isReversed
        ? checked
          ? GroupOptionDisableEnum.Disable
          : GroupOptionDisableEnum.Enable
        : checked
          ? GroupOptionDisableEnum.Enable
          : GroupOptionDisableEnum.Disable,
    } as Partial<GroupOptions>);
  };
  const handleSwitchChangeByKey = (key: GroupOptionKey, checked: boolean) => {
    if (!groupOptions || !client) return;
    const val = checked ? GroupOptionDisableEnum.Enable : GroupOptionDisableEnum.Disable;
    client.Api.session.setGroupOptions(groupOptions.group_id, {
      [key]: val,
    } as unknown as Partial<GroupOptions>);
  };

  const handleSwitchChangeByKeys = (checked: boolean) => {
    if (!groupOptions || !client) return;
    const val = checked ? GroupOptionDisableEnum.Enable : GroupOptionDisableEnum.Disable;
    client.Api.session.setGroupOptions(groupOptions.group_id, {
      [GroupOptionKey.RESTRICT_COPING]: val,
      [GroupOptionKey.RESTRICT_FORWARD]: val,
      [GroupOptionKey.RESTRICT_DOWNLOAD]: val,
      [GroupOptionKey.RESTRICT_SCREENSHOT]: val,
      [GroupOptionKey.RESTRICT_SCREEN_RECORDING]: val,
    } as unknown as Partial<GroupOptions>);
  };

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        run();
      } else {
        removeEventListener();
      }
    },
    [run, removeEventListener]
  );

  const handleSetAdminClick = async (member: GroupMember) => {
    if (!client) return;
    try {
      const result = await client.Api.session.updateGroupMemberRole(
        session!.chat_id,
        [member.user_id],
        GroupMemberRole.ADMIN
      );
      if (result?.update_fail_list?.length) {
        tools.toaster.error({
          title: t('tips.failedToAddAdmin', { count: result.update_fail_list.length }),
        });
        return;
      }
      tools.toaster.success({
        title: t('tips.addedToAdminSuccessfully', { count: 1 }),
      });
    } catch (error) {
      console.error('addGroupAdmin', error);
    }
  };
  const handleDeleteClick = async (member: GroupMember) => {
    if (!client) return;
    try {
      const result = await client.Api.session.updateGroupMemberRole(
        session!.chat_id,
        [member.user_id],
        GroupMemberRole.MEMBER
      );

      if (result?.update_fail_list?.length) {
        tools.toaster.error({
          title: t('tips.failedToRemoveAdmin', { count: result.update_fail_list.length }),
        });
        return;
      }
      tools.toaster.success({
        title: t('tips.removedFromAdminSuccessfully', { count: 1 }),
      });
    } catch (error) {
      console.error('removeGroupAdmin', error);
    }
  };

  return (
    <ContentDrawer
      title={t('chatSettings.groupManagementTitle')}
      disabled={disabledManagement}
      onOpenChange={onOpenChange}
      closeAllClick={closeAllClick}
      trigger={
        <div>
          <GroupItem icon={<ManagementIcon />} title={t('chatSettings.groupManagementTitle')} />
        </div>
      }
    >
      <Stack gap="2">
        <SettingItem title={t('chatSettings.groupAdminTitle')}>
          <GroupAdmins
            title={t('chatSettings.groupAdminsTitle')}
            members={members}
            maxAdminCount={MAX_ADMIN_COUNT}
            trigger={
              <SettingSubItem
                title={t('chatSettings.groupAdministratorsTitle')}
                onClick={() => {}}
              >
                {adminStr}
              </SettingSubItem>
            }
            onSetAdminClick={handleSetAdminClick}
            onDeleteClick={handleDeleteClick}
            onAddAdmin={() => {
              setGroupAddAdminIsOpen(true, {
                session: session!,
                members: members || [],
                adminMembers,
                maxAdminCount: MAX_ADMIN_COUNT,
              });
            }}
          />
        </SettingItem>
        <SettingItem title={t('chatSettings.groupMemberManagement')}>
          <MemberNotifications
            title={t('chatSettings.newMemberNotifications')}
            desc={t('chatSettings.newMemberNotificationsDesc')}
            value={memberJoinNotice?.toString()}
            options={optionsWithNobody}
            trigger={
              <SettingSubItem title={t('chatSettings.newMemberNotifications')} pointer={true}>
                {getOptionLabel(memberJoinNotice?.toString(), optionsWithNobody)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.MEMBER_JOIN_NOTICE)}
          />
          <MemberNotifications
            title={t('chatSettings.memberLeaveNotifications')}
            desc={t('chatSettings.memberLeaveNotificationsDesc')}
            value={memberLeaveNotice?.toString()}
            options={optionsWithNobody}
            trigger={
              <SettingSubItem title={t('chatSettings.memberLeaveNotifications')} pointer={true}>
                {getOptionLabel(memberLeaveNotice?.toString(), optionsWithNobody)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.MEMBER_LEAVE_NOTICE)}
          />
        </SettingItem>
        <SettingItem title={t('chatSettings.permissionSettings')}>
          <MemberNotifications
            title={t('chatSettings.editGroupInfo')}
            desc={t('chatSettings.editGroupInfoDesc')}
            value={memberInfoEdit?.toString()}
            options={baseOptions}
            trigger={
              <SettingSubItem title={t('chatSettings.editGroupInfo')} pointer={true}>
                {getOptionLabel(memberInfoEdit?.toString(), baseOptions)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.INFO_EDIT)}
          />
          <MemberNotifications
            title={t('chatSettings.mentionAll')}
            desc={t('chatSettings.mentionAllDesc')}
            value={memberAtAll?.toString()}
            options={baseOptions}
            trigger={
              <SettingSubItem title={t('chatSettings.mentionAll')} pointer={true}>
                {getOptionLabel(memberAtAll?.toString(), baseOptions)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.AT_ALL)}
          />
          <MemberNotifications
            title={t('chatSettings.speakInGroup')}
            desc={t('chatSettings.speakInGroupDesc')}
            groupDisallowSendInfo={groupDisallowSendInfo}
            members={members}
            value={memberSendMessage?.toString()}
            options={baseOptions}
            showException={false}
            onAddException={() => {
              setGroupAddAdminIsOpen(true, {
                session: session!,
                members: members || [],
                exceptionProps: {
                  title: t('chatSettings.addException'),
                  isException: true,
                  exceptionList: [],
                },
              });
            }}
            trigger={
              <SettingSubItem title={t('chatSettings.speakInGroup')} pointer={true}>
                {getOptionLabel(memberSendMessage?.toString(), baseOptions)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.SEND_MESSAGE)}
          />
          <MemberNotifications
            title={t('chatSettings.startingGroupCalls')}
            desc={t('chatSettings.startingGroupCallsDesc')}
            value={memberMediaCall?.toString()}
            options={baseOptions}
            trigger={
              <SettingSubItem title={t('chatSettings.startingGroupCalls')} pointer={true}>
                {getOptionLabel(memberMediaCall?.toString(), baseOptions)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.MEDIA_CALL)}
          />
          <MemberNotifications
            title={t('chatSettings.startChatsInGroup')}
            desc={t('chatSettings.startChatsInGroupDesc')}
            value={memberPrivateMessage?.toString()}
            options={baseOptions}
            trigger={
              <SettingSubItem title={t('chatSettings.startChatsInGroup')} pointer={true}>
                {getOptionLabel(memberPrivateMessage?.toString(), baseOptions)}
              </SettingSubItem>
            }
            onChange={handleMemberNotificationChange(GroupOptionKey.PRIVATE_MESSAGE)}
          />
        </SettingItem>
        <SettingItem title={t('chatSettings.privacySettings')}>
          <SettingSubItem title={t('chatSettings.findGroupViaSearch')}>
            <Switch
              checked={convertToBoolean(groupGlobalSearch)}
              onCheckedChange={(checked) =>
                handleSwitchChange(GroupOptionKey.GLOBAL_SEARCH, checked)
              }
            />
          </SettingSubItem>
          <SettingSubItem
            title={t('chatSettings.membershipApproval')}
            desc={t('chatSettings.membershipApprovalDesc')}
          >
            <Switch
              checked={convertToBoolean(enterAudit)}
              onCheckedChange={(checked) =>
                handleSwitchChange(GroupOptionKey.ENTER_AUDIT, checked)
              }
            />
          </SettingSubItem>
          <SettingSubItem
            title={t('chatSettings.hideMembers')}
            desc={t('chatSettings.hideMembersDesc')}
          >
            <Switch
              checked={convertToBoolean(hideMember)}
              onCheckedChange={(checked) =>
                handleSwitchChange(GroupOptionKey.HIDE_MEMBER, checked)
              }
            />
          </SettingSubItem>
          <SettingSubItem
            title={
              <Box>
                {t('chatSettings.restrictedMode')}
                {3}/3
              </Box>
            }
            desc={t('chatSettings.restrictedModeDesc')}
            collapseChildren={
              restrictedMode ? (
                <>
                  <SettingSubItem title={t('chatSettings.disableCopying')}>
                    <Switch
                      checked={convertToBoolean(restrictCoping)}
                      onCheckedChange={(checked) =>
                        handleSwitchChangeByKey(GroupOptionKey.RESTRICT_COPING, checked)
                      }
                    />
                  </SettingSubItem>
                  <SettingSubItem title={t('chatSettings.disableForwarding')}>
                    <Switch
                      checked={convertToBoolean(restrictForward)}
                      onCheckedChange={(checked) =>
                        handleSwitchChangeByKey(GroupOptionKey.RESTRICT_FORWARD, checked)
                      }
                    />
                  </SettingSubItem>
                  <SettingSubItem title={t('chatSettings.disableDownloads')}>
                    <Switch
                      checked={convertToBoolean(restrictDownload)}
                      onCheckedChange={(checked) =>
                        handleSwitchChangeByKey(GroupOptionKey.RESTRICT_DOWNLOAD, checked)
                      }
                    />
                  </SettingSubItem>
                </>
              ) : null
            }
          >
            <Switch
              checked={restrictedMode}
              onCheckedChange={(checked) => handleSwitchChangeByKeys(checked)}
            />
          </SettingSubItem>
        </SettingItem>
        <TransferGroupOwner
          trigger={
            isGroupOwner ? (
              <Box>
                <SettingSubItem title={t('chatSettings.transferGroupOwner')} pointer={true} />
              </Box>
            ) : null
          }
          members={members}
          onMemberAvatarClick={onMemberAvatarClick}
          onTransferClick={onTransferGroupOwnerClick}
        />
      </Stack>
    </ContentDrawer>
  );
};

const MemoizedGroupManagement = memo(GroupManagement);
export { MemoizedGroupManagement as GroupManagement };
